import React, {useEffect, useState, useCallback} from 'react';
import Collapsible from '../../shared/collapsible/collapsible';
import Select from '../../shared/select/Select';
import "./ContactForm.css";

import submitContactForm from '../../../utilities/SubmitContactForm';
import {phoneNumberRegex, emailRegex} from '../../../utilities/FormUtilities';
import Filters from "../filters/Filters";
import {
  useGoogleReCaptcha
} from 'react-google-recaptcha-v3';

import config from '../../../config';

interface iProps {
  comment?: string;
  onSubmitCallback?: Function
  stateOptions: {label: string; value: string}[];
  classIOptions: string[];
  filters?: React.RefObject<React.ElementRef<typeof Filters>>;
}

const loadedHazmatOptions = ["Yes", "No"];

export default function ContactForm(props: iProps) {
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [selectedStates, setSelectedStates] = useState(new Set<string>());
  const [selectedClassIRailroads, setSelectedClassIRailroads] = useState(new Set<string>());
  const [_286kGrossRailLoad, set286kGrossRailLoad] = useState(false);
  const [selectedLoadedHazmatOptions, setSelectedLoadedHazmatOptions] = useState(new Set<string>());
  const [loaded, setLoaded] = useState(false);
  const [empty, setEmpty] = useState(false);
  const [commodity, setCommodity] = useState('');
  const [numberOfCars, setNumberOfCars] = useState('');
  const [startDate, setStartDate] = useState('');
  const [projectedLength, setProjectedLength] = useState('');
  const [additionalDetails, setAdditionalDetails] = useState(props.comment || '');

  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [reCaptchaToken, setReCaptchaToken] = useState<string>();
  const [responseMessage, setResponseMessage] = useState<string>();

  const {executeRecaptcha} = useGoogleReCaptcha();

  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) {
      console.log('Execute recaptcha not yet available');
      return;
    }
    const token = await executeRecaptcha('ContactUs');
    setReCaptchaToken(token);
  }, [executeRecaptcha]);

  useEffect(() => {
    handleReCaptchaVerify();
  }, [handleReCaptchaVerify]);

  const initForm = () => {
    if (props.filters?.current) {
      const snapshot = props.filters?.current.getFiltersSnapshot();
      if (snapshot.state) {
        setSelectedStates(snapshot.state);
      }

      if (snapshot.classIRailroad) {
        setSelectedClassIRailroads(snapshot.classIRailroad);
      }

      if (snapshot._286kCompatible && snapshot._286kCompatible.has('yes')) {
        set286kGrossRailLoad(true);
      }

      if (snapshot.hazmat) {
        const hazmatSet = snapshot.hazmat;
        if (hazmatSet.has('hl')) {
          setSelectedLoadedHazmatOptions(new Set(['Yes']));
          setLoaded(true);
        }

        if (hazmatSet.has('nhl')) {
          setLoaded(true);
        }

        if (hazmatSet.has('nhe') || hazmatSet.has('he')) {
          setEmpty(true);
        }
      }

    }
  }
  useEffect(initForm, [props.filters]);

  const onSubmit = () => {
    const isValidPhoneNumber = phoneNumber.match(phoneNumberRegex);
    const isValidEmail = email.match(emailRegex);

    const errorMessages: string[] = [];

    if (!reCaptchaToken) {
      errorMessages.push("ReCaptcha failed.")
    }
    if (!name) {
      errorMessages.push("Name is required.")
    }
    if (!isValidPhoneNumber) {
      errorMessages.push("Valid Phone Number is required.");
    }
    if (!isValidEmail) {
      errorMessages.push("Valid Email is required.");
    }

    setValidationErrors(errorMessages);
    const validForm = errorMessages.length === 0;

    if (!validForm || !reCaptchaToken) return;

    const subject = 'GeoConnect - Railcar Storage'
    const body = `Name: ${name}\n`
      + `Phone Number: ${phoneNumber}\n`
      + `Email: ${email}\n`
      + `Company Name: ${companyName}\n\n`
      + 'Request Details\n'
      + `States: ${Array.from(selectedStates).join(", ")}\n`
      + `Class I Railroads: ${Array.from(selectedClassIRailroads).join(", ")}\n`
      + `286k Gross Rail Load: ${_286kGrossRailLoad}\n`
      + `Loaded Hazmat: ${Array.from(selectedLoadedHazmatOptions).join(", ")}\n`
      + `Loaded: ${loaded}\n`
      + `Empty: ${empty}\n`

      + `Commodity/STCC: ${commodity}\n`
      + `Number of Cars for Storage: ${numberOfCars}\n`
      + `Projected Start Date: ${startDate}\n`
      + `Estimated Time for Storage: ${projectedLength}\n`
      + `Additional Details: ${additionalDetails}`;

    submitContactForm(reCaptchaToken, config.railcarStorage.contactFormId, subject, email, body).then(response => {
      response.json().then(json => {
        setResponseMessage(json.message);

        if (json.status === 'mail_sent' && props.onSubmitCallback) {
          props.onSubmitCallback();
        }
      });
    }).catch((err) => {
      console.log(err);
      setResponseMessage("A network error occured. Please try again later.")
    });
  }

  const onClassICheckboxChange = (event: any) => {
    const options = new Set(selectedClassIRailroads);
    if (event.target.checked) {
      options.add(event.target.value);
    } else {
      options.delete(event.target.value);
    }
    setSelectedClassIRailroads(options);
  }

  const onLoadedHazmatCheckboxChange = (event: any) => {
    const options = new Set(selectedLoadedHazmatOptions);
    if (event.target.checked) {
      options.add(event.target.value);
    } else {
      options.delete(event.target.value);
    }
    setSelectedLoadedHazmatOptions(options);
  }

  const onStateCheckboxChange = (evt: any) => {
    const options = new Set(selectedStates);

    if (evt.target.checked) {
      options.add(evt.target.value);
    } else {
      options.delete(evt.target.value);
    }
    setSelectedStates(options);
  };

  return (
    <div className="railcar-storage-contact-form">
      <div className='flex-form'>
        <div className="input-container">
          <input type='text'
            value={name}
            onChange={evt => {setName(evt.target.value)}}
            name='your-name'
            placeholder='*Name' />
        </div>
        <div className="input-container">
          <input type='text'
            value={phoneNumber}
            onChange={evt => {setPhoneNumber(evt.target.value)}}
            name='phone'
            placeholder='*Phone Number' />
        </div>
        <div className="input-container">
          <input type='text'
            value={email}
            onChange={evt => {setEmail(evt.target.value)}}
            name='e-mail'
            placeholder='*Email' />
        </div>
        <div className="input-container">
          <input type='text'
            value={companyName}
            onChange={evt => {setCompanyName(evt.target.value)}}
            name='Company-Name'
            placeholder='Company Name' />
        </div>
      </div>

      <Collapsible buttonLabel='Request Details'>
        <div className='details-section'>
          <div className="flex-form">
            <div className='input-container'>
              <Select label={'States'} labelPosition={'inline'} multiSelect={true}>
                <div className='state-select'>
                  {props.stateOptions?.map((option: any) =>
                    <div key={option.label} className="state-option">
                      <input
                        type="checkbox"
                        id={`contact-class-i-checkbox-${option.value}`}
                        value={option.value}
                        checked={selectedStates.has(option.value)}
                        onChange={onStateCheckboxChange} />
                      <label htmlFor={`contact-class-i-checkbox-${option.value}`}>{option.label}</label>
                    </div>
                  )}
                </div>
              </Select>
            </div>
            <div className='input-container checkbox-group'>
              <div className='label'>Class I Railroads</div>
              {props.classIOptions.map((option, index) =>
                <div key={index} className="checkbox-container">
                  <input type="checkbox"
                    id={`class-i-checkbox-${index}`}
                    onChange={onClassICheckboxChange}
                    checked={selectedClassIRailroads.has(option)}
                    value={option}
                  />
                  <label htmlFor={`class-i-checkbox-${index}`}>{option}</label>
                </div>
              )}
            </div>
            <div className="input-container">
              <div className="checkbox-container">
                <input type="checkbox"
                  id='286k-checkbox'
                  onChange={(event: any) => {
                    set286kGrossRailLoad(event.target.checked);
                  }}
                  checked={_286kGrossRailLoad}
                />
                <label htmlFor='286k-checkbox'>286K Gross Rail Load</label>
              </div>
            </div>
            <div className='input-container checkbox-group'>
              <div className='label'>Loaded Hazmat</div>
              {loadedHazmatOptions.map((option, index) =>
                <div key={index} className="checkbox-container">
                  <input type="checkbox"
                    id={`loaded-hazmat-checkbox-${index}`}
                    onChange={onLoadedHazmatCheckboxChange}
                    checked={selectedLoadedHazmatOptions.has(option)}
                    value={option}
                  />
                  <label htmlFor={`loaded-hazmat-checkbox-${index}`}>{option}</label>
                </div>
              )}
            </div>
            <div className="input-container">
              <div className="checkbox-container">
                <input type="checkbox"
                  id='loaded-checkbox'
                  onChange={(event: any) => {
                    setLoaded(event.target.checked);
                  }}
                  checked={loaded}
                />
                <label htmlFor='loaded-checkbox'>Loaded</label>
              </div>
              <div className="checkbox-container">
                <input type="checkbox"
                  id='empty-checkbox'
                  onChange={(event: any) => {
                    setEmpty(event.target.checked);
                  }}
                  checked={empty}
                />
                <label htmlFor='empty-checkbox'>Empty</label>
              </div>
            </div>
            <div className="input-container">
              <input type='text'
                value={commodity}
                onChange={evt => {setCommodity(evt.target.value)}}
                name='Commodity-STCC'
                placeholder='Commodity/STCC' />
            </div>
            <div className="input-container">
              <input type='text'
                value={numberOfCars}
                onChange={evt => {setNumberOfCars(evt.target.value)}}
                name='Number-of-Cars'
                placeholder='Number of Cars for Storage' />
            </div>
            <div className="input-container">
              <input type='text'
                value={startDate}
                onChange={evt => {setStartDate(evt.target.value)}}
                name='Projected-Start-Date'
                placeholder='Projected Start Date' />
            </div>
            <div className="input-container">
              <input type='text'
                value={projectedLength}
                onChange={evt => {setProjectedLength(evt.target.value)}}
                name='Projected-Length'
                placeholder='Estimated Time for Storage' />
            </div>
            <div className="input-container">
              <textarea value={additionalDetails}
                onChange={evt => {setAdditionalDetails(evt.target.value)}}
                name='text-area'
                placeholder='Additional Details' />
            </div>
          </div>
        </div>
      </Collapsible>
      <div className='small-font'>
        * Required Fields
      </div>
      <div className='small-font'>
        This site is protected by reCAPTCHA and the Google&nbsp;
        <a href="https://policies.google.com/privacy">Privacy Policy</a> and&nbsp;
        <a href="https://policies.google.com/terms">Terms of Service</a> apply.
      </div>
      {validationErrors.map((msg, index) =>
        <div key={index} className='small-font color-orange'>
          {msg}
        </div>
      )}
      <div className="input-container">
        <button onClick={onSubmit}>Submit</button>
      </div>
      {responseMessage &&
        <div>{responseMessage}</div>
      }
    </div>
  )
}